import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/auth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { APP_LOGO, APP_NAME } from "@/lib/brand";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success("Welcome back!");
      // Land on "/", which is itself gated by RequireAccess(dashboard): users
      // who hold dashboard.view see the Dashboard, while a dashboard-denied
      // user is redirected onward to their first accessible module (or the
      // /no-access page) by the same landing logic the rest of the app uses.
      nav("/");
    } catch (err) {
      toast.error((err as Error).message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-slate-950">
      <RoofingBackdrop />

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-white/10 bg-slate-900/70 backdrop-blur-xl">
        <CardHeader className="text-center space-y-3">
          <img
            src={APP_LOGO}
            alt={APP_NAME}
            className="mx-auto h-14 w-auto rounded-md bg-white/95 p-1.5 object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
          <CardTitle className="text-2xl text-white">{APP_NAME}</CardTitle>
          <CardDescription className="text-slate-400">
            Sign in to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-200">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
                className="bg-slate-800/60 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-200">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-slate-800/60 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-primary hover:opacity-90"
              disabled={loading}
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sign In
            </Button>
            <p className="text-xs text-slate-400 text-center pt-2">
              First-time setup? Call{" "}
              <span className="font-mono text-slate-200">
                POST /api/auth/bootstrap
              </span>{" "}
              to create the initial administrator.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Dark, premium roofing-themed login backdrop for NAK Roofing System.
 * A deep charcoal-to-slate gradient with a very subtle diagonal
 * standing-seam metal-roof texture and a faint warm light bloom — quiet
 * enough that the sign-in card stays the focus. Pure CSS/SVG, no assets.
 */
function RoofingBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Base gradient — lighter toward the upper right, like the reference */}
      <div className="absolute inset-0 bg-gradient-to-bl from-slate-700/70 via-slate-900 to-slate-950" />

      {/* Subtle standing-seam metal-roof texture */}
      <svg
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 1440 900"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="seam" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0f172a" stopOpacity="0.55" />
            <stop offset="50%" stopColor="#cbd5e1" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#0f172a" stopOpacity="0.55" />
          </linearGradient>
          <pattern
            id="seams"
            width="84"
            height="120"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(20)"
          >
            <rect width="84" height="120" fill="url(#seam)" />
            <rect x="0" width="2" height="120" fill="#e2e8f0" opacity="0.14" />
            <rect x="80" width="3" height="120" fill="#020617" opacity="0.45" />
          </pattern>
        </defs>
        <rect x="-200" y="-200" width="1840" height="1300" fill="url(#seams)" />
      </svg>

      {/* Faint warm bloom (brand amber) + cool depth */}
      <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-slate-500/10 blur-3xl" />

      {/* Darkening vignette so the card reads clearly */}
      <div className="absolute inset-0 bg-slate-950/45" />
    </div>
  );
}
